<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import type { Freq } from "@types"
import { useI18n } from "vue-i18n"
import { useResponsive } from "@core/composables"

// ================================
// TYPES
// ================================
/** Objet graphique Plotly (data + layout + config optionnel). Aligné sur `PlotlyChart`. */
export type PlotlyGraph = {
    data: object[]
    layout: Record<string, unknown>
    config?: object
}

/** Option du select de graphique (header). */
export type GraphOption = {
    name: string
    value: string
    icon?: string
    icon_color?: string
}

/** Message d'erreur empty-data. */
export type ErrorMessageEmptyData = {
    type: "empty-data"
    message: string
    period?: {
        freq: string
        date_from?: string | null
        date_to?: string | null
    }
}

/** Action pour DataChartMessage. */
export type ErrorMessageAction = {
    message: string
    type: string
    url?: string
    modal?: string
    freq?: string
}

/** Message d'erreur avec action (invalid_freq, invalid_condition, error_loading). */
export type ErrorMessageWithAction = {
    type: "invalid_freq" | "invalid_condition" | "error_loading"
    message: string
    action?: ErrorMessageAction | null
}

export type ErrorMessage = ErrorMessageEmptyData | ErrorMessageWithAction | null

/** Message du footer. */
export type FooterMessage = {
    message: string
    action?: ErrorMessageAction
    closable?: boolean
    severity?: "info" | "warn" | "error"
}

/** Source pour DialogSourceList (compatible BuildingSource/Source). */
export type ChartSource = Record<string, unknown> & { _id_source?: string }

// ================================
// PROPS & EMITS
// ================================
type Props = {
    /** Id du conteneur (ex: graph-${plotly_graph_id}). */
    cardId?: string
    /** Titre affiché dans l'en-tête. */
    title?: string
    /** Données Plotly à afficher. */
    graph?: PlotlyGraph | null
    /** Affiche le skeleton à la place du graphique. */
    loading?: boolean
    /** Hauteur minimale de la zone contenu. */
    contentMinHeight?: string
    /** Active le bouton d'export sur le graphique. */
    enableExport?: boolean
    /** Options du select (si length > 1, le select est affiché). */
    graphOptionList?: GraphOption[]
    /** Valeur sélectionnée (v-model). */
    modelGraph?: string
    /** Option sélectionnée pour afficher icône + nom dans le select. */
    graphOptionValue?: GraphOption | null
    /** Placeholder du select. */
    graphPlaceholder?: string
    /** Affiche le bouton supprimer. */
    showRemoveButton?: boolean
    /** Tooltip du bouton supprimer (HTML). */
    removeTooltip?: string
    /** Message d'erreur à afficher (contenu central ou message sous le graphique). */
    errorMessage?: ErrorMessage
    /** Id bâtiment (pour DataChartMessage redirects). */
    currentBuildingId?: string | null
    /** Id source (pour DataChartMessage redirects). */
    currentSourceId?: string | null
    /** Affiche le bouton "Accéder aux données". */
    showSourceButton?: boolean
    /** Label du bouton source. */
    sourceButtonLabel?: string
    /** Liste des sources pour le modal. */
    chartSourcesList?: ChartSource[]
    /** Visibilité du modal des sources (v-model). */
    modalVisible?: boolean
    /** Messages du footer. */
    footerMessages?: FooterMessage[]
    /** Désactive le bouton d’action du message d’erreur (ex. réessai pendant chargement). */
    errorActionDisabled?: boolean
    /**
     * Respecter le `xaxis.range` posé par l'appelant (transmis à `PlotlyChart`).
     * Additif : à `false`, rien ne change pour les appelants historiques.
     */
    preserveLayoutRange?: boolean
    /** Boutons de barre d'outils à retirer en plus (transmis à `PlotlyChart`). */
    modeBarButtonsToRemoveExtra?: string[]
    /** Nom du fichier de l'image exportée (transmis à `PlotlyChart`). */
    toImageFilename?: string
    /** Classes ajoutées au titre de l'entête. Vide par défaut : rendu inchangé. */
    titleClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    cardId: undefined,
    title: "",
    graph: null,
    loading: false,
    contentMinHeight: "20rem",
    enableExport: false,
    graphOptionList: () => [],
    modelGraph: "",
    graphOptionValue: null,
    graphPlaceholder: "Sélectionner un graphique",
    showRemoveButton: false,
    removeTooltip: "",
    errorMessage: null,
    currentBuildingId: null,
    currentSourceId: null,
    showSourceButton: false,
    sourceButtonLabel: "Accéder aux données de la source",
    chartSourcesList: () => [],
    modalVisible: false,
    footerMessages: () => [],
    errorActionDisabled: false,
    preserveLayoutRange: false,
    modeBarButtonsToRemoveExtra: () => [],
    toImageFilename: undefined,
    titleClass: "",
})

const emit = defineEmits<{
    "update:modelGraph": [value: string]
    "update:modalVisible": [value: boolean]
    "click:remove": []
    "click:dates": [payload: { date_from: string; date_to: string }]
    "click:freq": [freq: string]
    "click:retry": []
    "click:source": []
    loaded: []
    "click:export": []
    /**
     * Le cliquer-glisser de l'axe des temps, relayé tel quel depuis
     * `PlotlyChart`. Additif : un appelant qui ne l'écoute pas ne change pas.
     */
    relayout: [ev: Record<string, unknown> | undefined]
    /** Le double-clic, relayé tel quel depuis `PlotlyChart`. */
    doubleclick: []
}>()

const { t } = useI18n({
    messages: {
        fr: {
            remove_graph: "Masquer",
        },
    },
})

const { isSmallMobile } = useResponsive()

const showSelect = computed(() => (props.graphOptionList?.length ?? 0) > 1)
const showGraph = computed(
    () =>
        !props.loading &&
        props.graph &&
        (!props.errorMessage || props.errorMessage.type === "empty-data")
)

/** Période pour DataChartMessageEmptyData (freq typé Freq). */
const emptyDataPeriod = computed(() => {
    const err = props.errorMessage
    if (!err || err.type !== "empty-data") return { freq: "daily" as Freq }
    const p = (err as ErrorMessageEmptyData).period ?? { freq: "daily" }
    return {
        freq: p.freq as Freq,
        date_from: p.date_from ?? undefined,
        date_to: p.date_to ?? undefined,
    }
})
</script>

<template>
    <BaseCard
        :id="cardId"
        class="data-chart-list-item plotly-chart-card shadow-none border border-surface-300"
    >
        <template #content>
            <!-- Header -->
            <div class="data-chart-list-item__header-wrapper flex flex-col">
                <div
                    class="data-chart-list-item__header sm:flex-row flex-col gap-2 mb-6 flex sm:items-center justify-between"
                >
                    <!-- `flex-wrap` : les compléments de titre (pastilles,
                         puces, boutons) passent à la ligne quand la place
                         manque, au lieu de comprimer le titre. Sur une carte
                         de demi-largeur, « Enveloppe horaire (kW) » sortait
                         sur deux lignes pour laisser passer une puce. Sans
                         complément, rien ne change : une seule chose ne
                         retourne jamais à la ligne. -->
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <h2
                            class="text-lg font-semibold text-primary flex gap-x-3"
                            :class="titleClass"
                        >
                            {{ title }}
                        </h2>
                        <slot name="title-extra" />
                    </div>
                    <div
                        class="data-chart-list-item__header-button flex sm:flex-row flex-col sm:items-center gap-2 justify-between"
                    >
                        <BaseSelect
                            v-if="showSelect"
                            :model-value="modelGraph"
                            :options="graphOptionList"
                            option-label="name"
                            option-value="value"
                            :placeholder="graphPlaceholder"
                            @update:model-value="
                                emit('update:modelGraph', $event)
                            "
                        >
                            <template #value="slotProps">
                                <div
                                    v-if="slotProps.value"
                                    class="flex items-center gap-x-3"
                                >
                                    <BaseIcon
                                        v-if="graphOptionValue?.icon"
                                        :icon="graphOptionValue.icon"
                                        :color="graphOptionValue.icon_color"
                                    />
                                    <div>{{ graphOptionValue?.name }}</div>
                                </div>
                                <span v-else>{{ slotProps.placeholder }}</span>
                            </template>
                            <template #option="slotProps">
                                <div class="flex items-center gap-x-3">
                                    <BaseIcon
                                        v-if="slotProps.option?.icon"
                                        :icon="slotProps.option.icon"
                                        :color="slotProps.option.icon_color"
                                    />
                                    <div>{{ slotProps.option.name }}</div>
                                </div>
                            </template>
                        </BaseSelect>
                        <BaseButton
                            v-if="showRemoveButton"
                            icon="pi pi-times"
                            rounded
                            text
                            class="button"
                            v-tooltip.left="{
                                escape: false,
                                value: removeTooltip,
                                pt: { text: 'text-base' },
                            }"
                            @click="emit('click:remove')"
                            :label="isSmallMobile ? t('remove_graph') : ''"
                        />
                    </div>
                </div>
            </div>

            <!-- Graphique & Message -->
            <div
                class="data-chart-list-item__graph relative w-full pr-2"
                :style="{ minHeight: contentMinHeight }"
            >
                <BaseChartSkeleton v-if="loading" class="h-full w-full" />
                <PlotlyChart
                    v-else-if="showGraph && graph"
                    :graph="graph as any"
                    :enable-export="enableExport"
                    :preserve-layout-range="preserveLayoutRange"
                    :mode-bar-buttons-to-remove-extra="
                        modeBarButtonsToRemoveExtra
                    "
                    :to-image-filename="toImageFilename"
                    class="h-full w-full"
                    @loaded="emit('loaded')"
                    @click:export="emit('click:export')"
                    @relayout="emit('relayout', $event)"
                    @doubleclick="emit('doubleclick')"
                />
                <DataChartMessageEmptyData
                    v-if="errorMessage && errorMessage.type === 'empty-data'"
                    class="data-chart-list-item__graph-message absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2"
                    :message="errorMessage.message"
                    :period="emptyDataPeriod"
                    @click:dates="emit('click:dates', $event)"
                />
                <DataChartMessage
                    v-else-if="errorMessage"
                    class="data-chart-list-item__graph-message absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2"
                    :message="errorMessage.message"
                    :action="
                        (errorMessage as ErrorMessageWithAction).action ??
                        undefined
                    "
                    :current-building-id="currentBuildingId"
                    :current-source-id="currentSourceId"
                    :action-button-disabled="errorActionDisabled"
                    @click:freq="emit('click:freq', $event)"
                    @click:retry="emit('click:retry')"
                />
            </div>

            <!-- Footer -->
            <div
                v-if="
                    showSourceButton ||
                    (footerMessages?.length ?? 0) > 0 ||
                    $slots.footer
                "
                class="data-chart-list-item__footer flex flex-col gap-2 self-stretch pt-[18px] [&_.p-button]:ml-auto"
            >
                <BaseButton
                    v-if="showSourceButton"
                    :label="sourceButtonLabel"
                    class="data-chart-list-item__footer-button font-bold"
                    icon="pi pi-angle-right"
                    icon-pos="right"
                    severity="secondary"
                    text
                    @click="emit('click:source')"
                />
                <DialogSourceList
                    v-if="(chartSourcesList?.length ?? 0) > 0"
                    :sources="chartSourcesList ?? []"
                    :visible="modalVisible"
                    @update:visible="emit('update:modalVisible', $event)"
                />
                <DataChartMessage
                    v-for="(
                        { message, action, closable, severity }, index
                    ) in footerMessages"
                    :key="index"
                    class="!w-full"
                    :message="message"
                    :action="action"
                    :current-building-id="currentBuildingId"
                    :current-source-id="currentSourceId"
                    :closable="closable"
                    :severity="severity"
                />
                <slot name="footer" />
            </div>
        </template>
    </BaseCard>
</template>
